import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { invoicesApi } from "../lib/api";
import { firstLine, formatDate, formatInvoiceNumber, formatMoney } from "../lib/money";
import { useShopType } from "../lib/theme";
import { Spinner } from "../components/ui-modern";
import {
  Eyebrow,
  ModernButton,
  ModernEmptyState,
  ModernErrorBanner,
  ModernPagination,
  ModernSearchInput,
  ModernStatusPill,
  ModernTableCard,
  ModernTd,
  ModernTh,
} from "../components/ui-modern";

type SortColumn = "issuedOn" | "customerName" | "amountPaid" | "balanceDue" | "status";
type SortDirection = "asc" | "desc";
interface SortState {
  column: SortColumn;
  direction: SortDirection;
}
const DEFAULT_SORT: SortState = { column: "issuedOn", direction: "desc" };

// Same status vocabulary/values as InvoicesListPageClassic.tsx's own
// STATUS_OPTIONS (a <select>'s options there) — rendered here as filter
// PILLS instead, matching the mockup's `invoiceFilters` row (see
// `Bay2Bill Redesign.dc.html` lines ~394-403). Tone per pill reuses the
// exact same status->color mapping ModernStatusPill uses for the table's
// own Status column, so a selected filter's color always matches the rows
// it selects.
const INVOICE_FILTERS: { value: string; label: string; tone: { bg: string; fg: string } }[] = [
  { value: "", label: "All", tone: { bg: "#eef1f6", fg: "#4a5361" } },
  { value: "open", label: "Not Paid", tone: { bg: "#fdf3e0", fg: "#8a5c00" } },
  { value: "partially_paid", label: "Partially Paid", tone: { bg: "#e6f1f5", fg: "#0b5468" } },
  { value: "paid", label: "Paid in Full", tone: { bg: "#e7f4ea", fg: "#1a6b2c" } },
  { value: "void", label: "Void", tone: { bg: "#fdecea", fg: "#b3261e" } },
];

/**
 * The modern theme's Invoices list — see docs/FRONTEND_BACKLOG.md Round 60
 * and `Bay2Bill Redesign.dc.html` lines ~394-438 ("Invoices").
 *
 * Same data as InvoicesListPageClassic.tsx — identical query key and
 * filter/pagination state shape, duplicated here as a thin re-run of the
 * same `useQuery` (see WorkOrdersListPageModern.tsx's own comment on why
 * duplication over extraction, same reasoning applies here).
 *
 * The mockup's eyebrow reads "$7,412 outstanding" — a shop-wide total.
 * `invoicesApi.list` (this page's only data source, same as Classic)
 * returns only the current PAGE of invoices plus a row `total` count, not
 * an aggregate balance-due sum across every invoice — computing a real
 * outstanding total needs either a new report endpoint or summing every
 * page client-side (neither in this round's scope, matching
 * DashboardPageModern's own documented data-substitution). This shows
 * "{total} invoices" instead, the same honest substitution Work Orders'
 * "N jobs" eyebrow makes.
 *
 * Classic's "Show only archived invoices" checkbox and each row's
 * "Archived" tag are both preserved (same `onlyArchived` state, passed to
 * the same query) — the mockup doesn't show this control at all, but
 * dropping it would be a real feature regression, not just a restyle.
 */
export function InvoicesListPageModern() {
  const hasVehicles = useShopType() !== "detailing";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The dashboard links here with the synthetic "unpaid" bucket (= open +
  // partially paid); expand it so the matching pills light up as if clicked.
  const statuses = Array.from(
    new Set(searchParams.getAll("status").flatMap((s) => (s === "unpaid" ? ["open", "partially_paid"] : [s]))),
  );
  const [search, setSearch] = useState("");
  const [onlyArchived, setOnlyArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const { data, isLoading, error } = useQuery({
    queryKey: ["invoices", "list", statuses, search, onlyArchived, sort, page, pageSize],
    queryFn: () =>
      invoicesApi.list({
        status: statuses.length > 0 ? statuses : undefined,
        search: search.trim() || undefined,
        onlyArchived,
        sortBy: sort.column,
        sortDirection: sort.direction,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
  });

  // "All" (empty value) clears every selected pill; any other pill toggles
  // itself on/off, OR'd together with whatever else is already selected —
  // clicking an already-active pill again removes it (the owner's ask).
  function toggleStatus(value: string): void {
    const next = value === "" ? [] : statuses.includes(value) ? statuses.filter((s) => s !== value) : [...statuses, value];
    setSearchParams(next.map((s): [string, string] => ["status", s]));
    setPage(1);
  }

  function toggleSort(column: SortColumn): void {
    setSort((prev) => (prev.column === column ? { column, direction: prev.direction === "asc" ? "desc" : "asc" } : { column, direction: "asc" }));
    setPage(1);
  }

  const isDefaultSort = sort.column === DEFAULT_SORT.column && sort.direction === DEFAULT_SORT.direction;
  function sortIndicator(column: SortColumn): string {
    return sort.column === column ? (sort.direction === "asc" ? " ▲" : " ▼") : "";
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3.5">
        <div>
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">
            {data ? `${data.total} invoice${data.total === 1 ? "" : "s"}` : "…"}
          </Eyebrow>
          <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Invoices</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {INVOICE_FILTERS.map((f) => {
            const active = f.value === "" ? statuses.length === 0 : statuses.includes(f.value);
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => toggleStatus(f.value)}
                style={{ background: f.tone.bg, color: f.tone.fg, borderColor: active ? f.tone.fg : "transparent" }}
                className="h-[38px] rounded-full border-2 px-3.5 text-[13px] font-semibold"
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3.5">
        <ModernSearchInput
          className="max-w-sm flex-1"
          placeholder="Invoice #, customer name/number/phone, or amount"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          aria-label="Search invoices"
        />
        <label className="flex items-center gap-2 text-[13.5px] text-[#4a5361]">
          <input
            type="checkbox"
            checked={onlyArchived}
            onChange={(e) => {
              setOnlyArchived(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 rounded border border-[#e2e7ee]"
          />
          Show only archived invoices
        </label>
        <ModernButton type="button" variant="secondary" onClick={() => setSort(DEFAULT_SORT)} disabled={isDefaultSort}>
          Reset Sort
        </ModernButton>
      </div>

      {isLoading && <Spinner />}
      {error && <ModernErrorBanner message="Could not load invoices." />}
      {data && data.items.length === 0 && <ModernEmptyState>No invoices match yet.</ModernEmptyState>}

      {data && data.items.length > 0 && (
        <ModernTableCard>
          <table className="w-full min-w-[700px] border-collapse text-[14px]">
            <thead>
              <tr className="bg-[#f8fafc]">
                <ModernTh onClick={() => toggleSort("issuedOn")}>Invoice{sortIndicator("issuedOn")}</ModernTh>
                <ModernTh>{hasVehicles ? "Vehicle & concern" : "Concern"}</ModernTh>
                <ModernTh align="right" onClick={() => toggleSort("amountPaid")}>
                  Paid{sortIndicator("amountPaid")}
                </ModernTh>
                <ModernTh align="right" onClick={() => toggleSort("balanceDue")}>
                  Due{sortIndicator("balanceDue")}
                </ModernTh>
                <ModernTh onClick={() => toggleSort("status")}>Status{sortIndicator("status")}</ModernTh>
              </tr>
            </thead>
            <tbody>
              {data.items.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => navigate(`/invoices/${inv.invoiceNumber}`)}
                  className="cursor-pointer transition-colors hover:bg-[#f1f7f9]"
                >
                  <ModernTd>
                    <Link
                      to={`/invoices/${inv.invoiceNumber}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-[#071a33] no-underline"
                    >
                      {inv.customerNameSnapshot}
                    </Link>
                    <div
                      className="mt-1 font-mono text-[11.5px] leading-tight text-[#8b97a8]"
                      style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                    >
                      {formatInvoiceNumber(inv.invoiceNumber)} · {formatDate(inv.issuedOn)}
                    </div>
                    {inv.archivedAt && (
                      <span className="mt-1 inline-block rounded-[6px] bg-[#f1f4f9] px-2 py-0.5 text-[11px] font-semibold text-[#6b7a8f]">
                        Archived
                      </span>
                    )}
                  </ModernTd>
                  <ModernTd>
                    {hasVehicles && (
                      <div className="text-[13.5px] text-[#101b2d]">
                        {inv.vehicleDescriptionSnapshot ? firstLine(inv.vehicleDescriptionSnapshot) : "—"}
                      </div>
                    )}
                    {inv.customerConcernSnapshot ? (
                      <div className={hasVehicles ? "mt-1 text-[12.5px] text-[#6b7a8f]" : "text-[13.5px] text-[#101b2d]"}>
                        {inv.customerConcernSnapshot}
                      </div>
                    ) : (
                      !hasVehicles && <div className="text-[13.5px] text-[#8b97a8]">—</div>
                    )}
                  </ModernTd>
                  <ModernTd align="right" className="tabular-nums text-[#1e7d32]">
                    {formatMoney(inv.amountPaid)}
                  </ModernTd>
                  <ModernTd align="right" className={`font-bold tabular-nums ${inv.balanceDue !== "0.00" ? "text-[#b3261e]" : "text-[#071a33]"}`}>
                    {formatMoney(inv.balanceDue)}
                  </ModernTd>
                  <ModernTd>
                    <ModernStatusPill status={inv.status} />
                  </ModernTd>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#e2e7ee] bg-[#f8fafc] font-bold text-[#071a33]">
                <ModernTd colSpan={2}>
                  Totals · {data.total} invoice{data.total === 1 ? "" : "s"}
                  <span className="ml-2 text-[12.5px] font-semibold text-[#6b7a8f]">Invoiced {formatMoney(data.sums.total)}</span>
                </ModernTd>
                <ModernTd align="right" className="tabular-nums text-[#1e7d32]">
                  {formatMoney(data.sums.amountPaid)}
                </ModernTd>
                <ModernTd align="right" className={`tabular-nums ${data.sums.balanceDue !== "0.00" ? "text-[#b3261e]" : ""}`}>
                  {formatMoney(data.sums.balanceDue)}
                </ModernTd>
                <ModernTd>{null}</ModernTd>
              </tr>
            </tfoot>
          </table>
          <ModernPagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </ModernTableCard>
      )}
    </div>
  );
}
